'use client';
import React, { useCallback, startTransition } from 'react';

import { ArticleData, ArticleManager, getInfiniteScrollArticlesIDs } from '@benzinga/article-manager';


interface useInfiniteArticlesReturn {
  infiniteScrollArticles: ArticleData[];
  infiniteScrollArticlesIDs: number[];
  loadInfiniteArticle: (index: number) => void;
}

interface HookOptions {
  initialCount: number;
}

const DEFAULT_INITIAL_COUNT = 4;

export const useInfiniteArticles = (
  articleData: ArticleData,
  session,
  options: Partial<HookOptions> = { initialCount: DEFAULT_INITIAL_COUNT },
): useInfiniteArticlesReturn => {
  const [infiniteScrollArticles, setInfiniteScrollArticles] = React.useState<ArticleData[]>([]);
  const [infiniteScrollArticlesIDs, setInfiniteScrollArticlesIDs] = React.useState<number[]>([]);

  const loadArticlesWithIDs = useCallback(
    (articlesIDs: number[]) => {
      const articleManager = session.getManager(ArticleManager);
      articleManager
        .getArticlesWithIds(articlesIDs.slice(0, options?.initialCount || DEFAULT_INITIAL_COUNT))
        .then(articleResponse => {
          if (articleResponse?.ok) {
            startTransition(() => {
              setInfiniteScrollArticles(infiniteScrollArticles => [...infiniteScrollArticles, ...articleResponse.ok]);
            });
          }
        })
        .catch(error => {
          console.error('Infinite Article Fetch Error', error);
        });
    },
    [session, options],
  );

  const loadInitialArticlesIDs = useCallback(() => {
    const articleManager = session.getManager(ArticleManager);
    return getInfiniteScrollArticlesIDs(articleManager, articleData)
      .then(articlesIDs => {
        startTransition(() => {
          setInfiniteScrollArticlesIDs(articlesIDs);
        });

        return articlesIDs;
      })
      .catch(error => {
        console.error('Infinite Article IDs Fetch Error', error);
      });
  }, [articleData, session]);

  const loadInfiniteArticle = index => {
    const articleID = infiniteScrollArticlesIDs[index];

    if (articleID) {
      const articleManager = session.getManager(ArticleManager);
      articleManager.getArticle(articleID).then(article => {
        if (article?.ok) {
          startTransition(() => {
            setInfiniteScrollArticles(infiniteScrollArticles => [...infiniteScrollArticles, article.ok]);
          });
        }
      });
    }
  };

  React.useEffect(() => {
    if (articleData) {
      setTimeout(() => {
        loadInitialArticlesIDs().then(articleIds => {
          loadArticlesWithIDs(articleIds || []);
        });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    infiniteScrollArticles,
    infiniteScrollArticlesIDs,
    loadInfiniteArticle,
  };
};
